import {t, Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {TextAreaBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLocalize} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {CreateFormInputDisplay} from '../../../../../components/common/createFormInputs/createFormInputDisplay';
import {useLinkNavigation} from '../../../../../components/common/navigation/useLinkNavigation';
import {LocalApplicationModel} from '../../../../../models/api/localApplicationModel';
import {SettingsDevelopersApplicationInfoUrl} from '../../../../../models/url/settingsUrlModel';
import {SettingsDevelopersApplicationInfoIcon} from './settingsDevelopersApplicationInfoIcon';
import {SettingsDevelopersApplicationReadonlyFields} from './settingsDevelopersApplicationReadonlyFields';

/*
 * Constants.
 */

const applicationPublishingDocsUrl =
  'https://dev.frontapp.com/docs/partnering-with-front#2-submit-your-integration';

/*
 * Props.
 */

interface SettingsDevelopersApplicationInfoPageProps {
  url: SettingsDevelopersApplicationInfoUrl;
  application: LocalApplicationModel;
}

/*
 * Style.
 */

const StyledInfoWrapperDiv = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 0 1px 64px 1px;
  overflow: auto;
  overflow-x: hidden;
`;

const StyledAppNameTextBox = styled(TextBox)`
  max-width: 230px;
`;

const StyledDescriptionTextAreaBox = styled(TextAreaBox)`
  width: 400px;
`;

const StyledPublishDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  padding-top: 20px;
  margin-top: 20px;
`;

const StyledPublishDetailsDiv = styled.div`
  flex: 1;
  gap: 12px;
  color: ${(p) => p.theme.greys.shade60};
  line-height: 22px;
`;

const StyledPublishTitleDiv = styled.div`
  font-size: ${fontSizes.large};
  line-height: 19px;
  color: ${(p) => p.theme.greys.shade90};
  margin-bottom: 4px;
`;

const StyledPublishDetailsActionDiv = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade80};
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationInfoPage: FC<SettingsDevelopersApplicationInfoPageProps> = (
  props,
) => {
  const {application} = props;
  const localize = useLocalize();
  const {navigateToLink} = useLinkNavigation();

  const [appName, setAppName] = useState(application.name);
  const [description, setDescription] = useState(application.description ?? '');

  return (
    <StyledInfoWrapperDiv>
      <SettingsDevelopersApplicationInfoIcon application={application} />
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-info-app-name-title">App name</Trans>}
        input={
          <StyledAppNameTextBox
            value={appName}
            onChange={setAppName}
            isRequired
            noMargin
            placeholder={localize(t('developers-application-info-app-name-placeholder')`Add app name`)}
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-info-short-description-title">Short description</Trans>}
        input={
          <StyledDescriptionTextAreaBox
            value={description}
            onChange={setDescription}
            isRequired
            noMargin
            shouldAllowResize
            placeholder={localize(
              t('developers-application-info-short-description-placeholder')`Add short description`,
            )}
          />
        }
      />
      <SettingsDevelopersApplicationReadonlyFields application={application} />
      <StyledPublishDetailsWrapperDiv>
        <StyledPublishDetailsDiv>
          <StyledPublishTitleDiv>
            <Trans id="developers-application-info-publish-details-title">Publish app</Trans>
          </StyledPublishTitleDiv>
          <Trans id="developers-application-info-publish-details-description">
            Reference our Developer portal docs to learn how you can publish your app for all Front customers.
          </Trans>
        </StyledPublishDetailsDiv>
        <StyledPublishDetailsActionDiv>
          <Button
            onClick={(event) => {
              event.preventDefault();
              navigateToLink(applicationPublishingDocsUrl);
            }}
          >
            <StyledButtonContentWrapperDiv>
              <StyledIcon name="externalLinkSmall" />
              <Trans id="developers-application-info-publish-details-action">Learn how to publish</Trans>
            </StyledButtonContentWrapperDiv>
          </Button>
        </StyledPublishDetailsActionDiv>
      </StyledPublishDetailsWrapperDiv>
    </StyledInfoWrapperDiv>
  );
};
